import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Grid,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  VpnKey as LicenseIcon,
  People as UserIcon,
  Devices as DeviceIcon,
  Timeline as ActivityIcon,
} from '@mui/icons-material';
import { fetchDashboardData } from '../../api/dashboard';
import type { DashboardData } from '../../types/dashboard';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';

const StatCard: React.FC<{
  title: string;
  value: number;
  icon: React.ReactNode;
}> = ({ title, value, icon }) => (
  <Card>
    <CardContent>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box>
          <Typography color="textSecondary" gutterBottom>
            {title}
          </Typography>
          <Typography variant="h4">{value}</Typography>
        </Box>
        <Box color="primary.main">{icon}</Box>
      </Box>
    </CardContent>
  </Card>
);

const DashboardPage: React.FC = () => {
  const { data, isLoading, error } = useQuery<DashboardData, Error>({
    queryKey: ['dashboard'],
    queryFn: () => fetchDashboardData(),
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography color="error">データの読み込みに失敗しました</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom data-testid="dashboard-title">
        ダッシュボード
      </Typography>

      <Grid container spacing={3}>
        {/* 統計カード */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="総ライセンス数"
            value={data?.stats.totalLicenses || 0}
            icon={<LicenseIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="アクティブライセンス"
            value={data?.stats.activeLicenses || 0}
            icon={<LicenseIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="総ユーザー数"
            value={data?.stats.totalUsers || 0}
            icon={<UserIcon fontSize="large" />}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="総デバイス数"
            value={data?.stats.totalDevices || 0}
            icon={<DeviceIcon fontSize="large" />}
          />
        </Grid>

        {/* ライセンスステータス */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom data-testid="license-status-title">
              ライセンスステータス
            </Typography>
            <List>
              {data?.licenseStatuses.map((status) => (
                <ListItem key={status.status}>
                  <ListItemText
                    primary={status.status === 'ACTIVE' ? 'アクティブ' :
                            status.status === 'INACTIVE' ? '非アクティブ' :
                            status.status === 'EXPIRED' ? '期限切れ' : '取り消し済み'}
                    secondary={`${status.count}件`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>

        {/* 最近のアクティビティ */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom data-testid="recent-activities-title">
              最近のアクティビティ
            </Typography>
            <List>
              {data?.recentActivities.map((activity) => (
                <ListItem key={activity.id}>
                  <ListItemText
                    primary={activity.message}
                    secondary={`${format(new Date(activity.timestamp), 'yyyy/MM/dd HH:mm', { locale: ja })}`}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardPage; 